import { type FC, useState } from 'react'
import styled from 'styled-components'

import { Portal } from '@/@bothrs-ui/web/components/portal/Portal'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { useCareQuestionOptions } from '@/modules/care-question/hooks/useCareQuestionOptions'
import { CareQuestionBottomSheet } from './care-question-bottom-sheet/CareQuestionBottomSheet'

/**
 * Action button for care questions options
 * Displayed as a button with a dropdown (for mobile)
 */
export const CareQuestionMobileActionButton: FC = () => {
  // State
  // ------------------------------------------------------------------------- /
  const [showBottomSheet, setShowBottomSheet] = useState(false)

  // Hooks
  // ------------------------------------------------------------------------- /
  const careQuestionOptions = useCareQuestionOptions()

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleToggleBottomSheet = () => {
    if (showBottomSheet) {
      mixpanel.track('care_question_close_bottom_sheet')
    } else {
      mixpanel.track('care_question_open_bottom_sheet')
    }

    setShowBottomSheet(!showBottomSheet)
  }

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <>
      {/* Portal the FAB out of the page-transition container. ContentPageLayout
          animates its content with a scale transform, and a transform establishes
          the containing block for position: fixed — so anchoring the FAB to the
          viewport requires rendering it outside that transformed ancestor. */}
      <Portal wrapperId='fab-container'>
        <StFloatingButton
          icon={['fal', 'plus']}
          size='large'
          buttonSize={48}
          variant={'Primary'}
          borderRadius={24}
          onClick={handleToggleBottomSheet}
        />
      </Portal>

      {/* Keep the bottom sheet outside the FAB portal. BottomSheet portals
          itself to #bottom-sheet-container and registers that wrapper on its
          first render to keep it ahead of #modal-container in the DOM. Nesting
          it under another Portal delays that first mount by a render (Portal
          renders null until its layout effect runs) and can break stacking. */}
      <CareQuestionBottomSheet
        careQuestionOptions={careQuestionOptions}
        visible={showBottomSheet}
        onClose={handleToggleBottomSheet}
      />
    </>
  )
}

const StFloatingButton = styled(IconButton)<{
  buttonSize: number
  borderRadius: number
}>`
  position: fixed;
  right: ${({ theme }) => theme.UI.Spacing.Space6}px;
  bottom: ${({ theme }) => theme.UI.Spacing.Space4 + 80}px;

  margin-bottom: var(--safe-area-inset-bottom);

  @media ${breakpoints.fromS} {
    display: none;
  }

  z-index: 1000;

  width: ${({ buttonSize }) => buttonSize}px;
  height: ${({ buttonSize }) => buttonSize}px;
  border-radius: ${({ borderRadius }) => borderRadius}px;

  background-color: ${({ color }) => color};

  color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
`
